import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { authKeys, resendVerificationEmail, verifyEmail } from '@/api/auth';
import { useAuth } from '@/providers/AuthProvider';
import { ApiException } from '@/types/api';

type Status = 'idle' | 'verifying' | 'verified' | 'failed';

/**
 * Email verification — BUILD-PLAN §17, §41.
 *
 * Serves two arrivals with one route:
 *   • from the emailed link, carrying signed parameters → verify immediately
 *   • straight after registration, with no parameters → explain and offer a
 *     resend
 */
export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { isAuthenticated, isVerified } = useAuth();

  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const id = searchParams.get('id');
  const hash = searchParams.get('hash');
  const expires = searchParams.get('expires');
  const signature = searchParams.get('signature');

  useEffect(() => {
    if (!id || !hash || !expires || !signature) {
      return;
    }

    let cancelled = false;

    setStatus('verifying');

    verifyEmail({ id, hash, expires, signature })
      .then(() => {
        if (cancelled) return;

        setStatus('verified');
        // The cached user still says unverified; refresh so the rest of the
        // app reflects the new state without a reload.
        void queryClient.invalidateQueries({ queryKey: authKeys.user });
      })
      .catch((error: unknown) => {
        if (cancelled) return;

        setStatus('failed');
        setMessage(
          error instanceof ApiException
            ? error.message
            : 'We could not confirm your email address.',
        );
      });

    // Guards against a state update after unmount if the customer navigates
    // away mid-request.
    return () => {
      cancelled = true;
    };
  }, [id, hash, expires, signature, queryClient]);

  async function handleResend() {
    setResending(true);

    try {
      await resendVerificationEmail();
      setResent(true);
    } catch {
      setMessage('We could not send another email just now. Please try again shortly.');
    } finally {
      setResending(false);
    }
  }

  if (status === 'verifying') {
    return (
      <AuthLayout title="Confirming your email">
        <Alert tone="info">One moment while we confirm your email address…</Alert>
      </AuthLayout>
    );
  }

  if (status === 'verified' || (status === 'idle' && isVerified)) {
    return (
      <AuthLayout title="Your email is confirmed" description="Thank you — your account is ready.">
        <ButtonLink to="/account" fullWidth size="lg">
          Go to My Account
        </ButtonLink>
      </AuthLayout>
    );
  }

  if (status === 'failed') {
    return (
      <AuthLayout
        title="We couldn’t confirm that link"
        footer={
          <Link to="/" className="text-accent underline underline-offset-4">
            Back to home
          </Link>
        }
      >
        <div className="flex flex-col gap-5">
          <Alert tone="error">{message}</Alert>

          {/* Resending requires a session, so only offer it when signed in. */}
          {isAuthenticated && !resent && (
            <Button onClick={handleResend} loading={resending} fullWidth>
              Send a New Link
            </Button>
          )}

          {resent && <Alert tone="success">A new confirmation email is on its way.</Alert>}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Confirm your email"
      description="We’ve sent a confirmation link to your email address. Please open it to finish setting up your account."
      footer={
        <Link to="/" className="text-accent underline underline-offset-4">
          Back to home
        </Link>
      }
    >
      <div className="flex flex-col gap-5">
        {message && <Alert tone="error">{message}</Alert>}

        {resent ? (
          <Alert tone="success">A new confirmation email is on its way.</Alert>
        ) : (
          isAuthenticated && (
            <Button onClick={handleResend} loading={resending} variant="secondary" fullWidth>
              Resend Confirmation Email
            </Button>
          )
        )}
      </div>
    </AuthLayout>
  );
}
